import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Colors, CompoundProps, Sizes } from '@/components/ui/types';

import Fieldset from '../fieldset';
import { RadioProps } from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'radio-xs',
  sm: 'radio-sm',
  md: 'radio-md',
  lg: 'radio-lg',
  xl: 'radio-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'radio-neutral',
  primary: 'radio-primary',
  secondary: 'radio-secondary',
  accent: 'radio-accent',
  info: 'radio-info',
  success: 'radio-success',
  warning: 'radio-warning',
  error: 'radio-error',
};

const RadioBase = forwardRef<HTMLInputElement, RadioProps>(
  ({ className, size = 'md', color = 'default', ...props }, ref) => {
    const classes = twMerge(
      'radio',
      sizeClasses[size],
      colorClasses[color],
      className,
    );

    return <input ref={ref} type="radio" className={classes} {...props} />;
  },
);

const RadioGroup = forwardRef<HTMLDivElement, CompoundProps>(
  ({ children, className, ...props }, ref) => {
    const classes = twMerge('flex flex-col gap-6', className);
    return (
      <div ref={ref} role="radiogroup" className={classes} {...props}>
        {children}
      </div>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';
RadioBase.displayName = 'Radio';

export const Radio = Object.assign(RadioBase, {
  Fieldset,
  Group: RadioGroup,
});
